import Navbar from "../componenets/Navbar";
import Sidebar from "../componenets/Sidebar";
import React, { useState } from 'react';
import { Link } from "react-router-dom";
import { utils, writeFile } from 'xlsx';
import swal from "sweetalert";

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Button from '@mui/material/Button';
import useKawasans from "../componenets/useKawasans";

import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#596CFF",
    fontWeight: "bold",
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://www.globizsystems.com/';

function AllCustomers() {
  const [customerType, setCustomerType] = useState('air');
  const [selectedScript, setSelectedScript] = useState('');
  const [accountSearch, setAccountSearch] = useState('');
  const [customer, setCustomer] = useState(null);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [areaLocation, setAreaLocation] = useState('');
  const [area, setArea] = useState([]);
  const kawasanOptions = useKawasans(customerType);

  const typeLabel = customerType === 'sewa' ? 'Sewa' : 'Air';
  const selectedAreas = selectedScript ? kawasanOptions[selectedScript] || [] : [];

  const resetResults = () => {
    setSelectedScript('');
    setAccountSearch('');
    setCustomer(null);
    setSearchPerformed(false);
    setAreaLocation('');
    setArea([]);
  };

  const changeCustomerType = (type) => {
    if (type === customerType) return;
    setCustomerType(type);
    resetResults();
  };

  async function findCustomer(event) {
    const value = event.target.value;
    const account_no = value.trim();

    setAccountSearch(value);

    if (!account_no) {
      setCustomer(null);
      setSearchPerformed(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/customers/get_single_customer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_no, type: customerType })
      });

      if (!response.ok) {
        setCustomer(null);
        setSearchPerformed(true);
        return;
      }

      const responseJson = await response.json();
      setCustomer(responseJson);
      setSearchPerformed(true);
    } catch (error) {
      console.error(error);
      setCustomer(null);
      setSearchPerformed(true);
    }
  }

  async function findCustomerLocation(event) {
    const kod_kawasan = event.target.value;
    setAreaLocation(kod_kawasan);

    if (!kod_kawasan) {
      setArea([]);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/customers/get_all_customer_by_location`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kod_kawasan, type: customerType })
      });

      if (!response.ok) {
        throw new Error('Unable to load customers for this area.');
      }

      const responseJson = await response.json();
      setArea(Array.isArray(responseJson) ? responseJson : []);
    } catch (error) {
      console.error(error);
      setArea([]);
      swal('Search failed', error.message, 'error');
    }
  }

  function findScript(event) {
    setSelectedScript(event.target.value);
    setAreaLocation('');
    setArea([]);
  }

  async function deleteCustomer(accountNumber) {
    const confirmed = await swal({
      title: 'Delete customer?',
      text: `This will delete the selected ${typeLabel} customer.`,
      icon: 'warning',
      buttons: ['Cancel', 'Delete'],
      dangerMode: true,
    });

    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/customers/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: String(accountNumber), type: customerType })
      });

      const responseJson = await response.json();

      if (!response.ok) {
        throw new Error(responseJson.error || responseJson.message || 'Unable to delete customer.');
      }

      setArea((current) => current.filter((item) => getAccountNumber(item) !== String(accountNumber)));

      if (customer && getAccountNumber(customer) === String(accountNumber)) {
        setCustomer(null);
        setSearchPerformed(false);
        setAccountSearch('');
      }

      swal('Success', 'Customer deleted', 'success', {
        buttons: false,
        timer: 1800,
      });
    } catch (error) {
      console.error(error);
      swal('Delete failed', error.message, 'error');
    }
  }

  const getAccountNumber = (item) => String(
    customerType === 'sewa' ? (item?.no_akaun ?? '') : (item?.no_akaun_air ?? '')
  );

  const getCustomerName = (item) => customerType === 'sewa' ? item?.name : item?.nama;
  const getUnitNumber = (item) => customerType === 'sewa' ? item?.unit_no : item?.no_unit;
  const getPrimaryPhone = (item) => customerType === 'sewa' ? item?.no_telephone : item?.no_telefon_1;
  const getKawasanName = (item) => customerType === 'sewa' ? item?.kawasan : item?.kawasan_sewaan;

  const exportCustomersByArea = () => {
    if (!areaLocation) {
      swal({
        title: 'Choose an area first',
        text: 'Select a script and location before downloading the customer list.',
        icon: 'warning'
      });
      return;
    }

    const rows = area.map((item) => {
      if (customerType === 'sewa') {
        return {
          ID: item.id,
          NAMA: item.name,
          'NO. AKAUN SEWA': item.no_akaun,
          'NO. UNIT': item.unit_no,
          'NO. TELEFON': item.no_telephone,
          'KATEGORI SEWAAN': item.category_sewaan,
          'KAWASAN SEWAAN': item.kawasan,
          STATUS: item.status,
          'KADAR SEWA': item.kadar_sewa,
          TUNGGAKAN: item.tunggakan,
          BAYARAN: item.bayaran,
          'BAKI AKHIR': item.baki_akhir,
        };
      }

      return {
        ID: item.id,
        NAMA: item.nama,
        'NO. AKAUN AIR': item.no_akaun_air,
        'NO. UNIT': item.no_unit,
        'NO. TELEFON (1)': item.no_telefon_1,
        'NO. TELEFON (2)': item.no_telefon_2,
        'KAWASAN SEWAAN': item.kawasan_sewaan,
      };
    });

    const wb = utils.book_new();
    const ws = utils.json_to_sheet(rows);
    utils.book_append_sheet(wb, ws, `${typeLabel} Customers`);

    const safeArea = String(areaLocation).replace(/[^a-zA-Z0-9_-]+/g, '_');
    writeFile(wb, `${typeLabel}_Customers_${safeArea}.xlsx`);
  };

  const renderTableHeader = () => (
    <TableHead>
      <TableRow>
        <StyledTableCell>ID</StyledTableCell>
        <StyledTableCell>NAMA</StyledTableCell>
        <StyledTableCell align="center">
          {customerType === 'sewa' ? 'NO. AKAUN SEWA' : 'NO. AKAUN AIR'}
        </StyledTableCell>
        <StyledTableCell align="center">NO. UNIT</StyledTableCell>
        <StyledTableCell align="center">NO. TELEFON</StyledTableCell>
        {customerType === 'air' && (
          <StyledTableCell align="center">NO. TELEFON (2)</StyledTableCell>
        )}
        {customerType === 'sewa' && (
          <StyledTableCell align="center">KATEGORI SEWAAN</StyledTableCell>
        )}
        <StyledTableCell align="center">KAWASAN SEWAAN</StyledTableCell>
        {customerType === 'sewa' && (
          <StyledTableCell align="center">STATUS</StyledTableCell>
        )}
        <StyledTableCell align="center">ACTIONS</StyledTableCell>
      </TableRow>
    </TableHead>
  );

  const renderCustomerRow = (item, keyPrefix = '') => {
    const accountNumber = getAccountNumber(item);

    return (
      <StyledTableRow key={`${keyPrefix}${customerType}-${item.id}-${accountNumber}`}>
        <StyledTableCell>{item.id}</StyledTableCell>
        <StyledTableCell>{getCustomerName(item)}</StyledTableCell>
        <StyledTableCell align="center">{accountNumber}</StyledTableCell>
        <StyledTableCell align="center">{getUnitNumber(item)}</StyledTableCell>
        <StyledTableCell align="center">{getPrimaryPhone(item)}</StyledTableCell>
        {customerType === 'air' && (
          <StyledTableCell align="center">{item.no_telefon_2}</StyledTableCell>
        )}
        {customerType === 'sewa' && (
          <StyledTableCell align="center">{item.category_sewaan}</StyledTableCell>
        )}
        <StyledTableCell align="center">{getKawasanName(item)}</StyledTableCell>
        {customerType === 'sewa' && (
          <StyledTableCell align="center">{item.status}</StyledTableCell>
        )}
        <StyledTableCell align="center">
          {customerType === 'air' && (
            <Link
              to={`/editcustomer/${item.id}`}
              className="btn btn-primary col-10"
              style={{ color: 'white', marginBottom: '4px' }}
            >
              Edit
            </Link>
          )}
          <button
            type="button"
            className="btn btn-danger col-10"
            onClick={() => deleteCustomer(accountNumber)}
          >
            Delete
          </button>
        </StyledTableCell>
      </StyledTableRow>
    );
  };

  const renderCustomersTable = (items, single = false) => (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 700 }} aria-label={`${customerType} customers table`}>
        {renderTableHeader()}
        <TableBody>
          {single ? (
            customer ? renderCustomerRow(customer, 'single-') : (
              <StyledTableRow>
                <StyledTableCell colSpan={customerType === 'sewa' ? 9 : 8} align="center">
                  No customer found
                </StyledTableCell>
              </StyledTableRow>
            )
          ) : (
            area.length > 0 ? area.map((item) => renderCustomerRow(item, 'area-')) : (
              <StyledTableRow>
                <StyledTableCell colSpan={customerType === 'sewa' ? 9 : 8} align="center">
                  {areaLocation ? 'No customers found for this area' : 'Choose an area to display customers'}
                </StyledTableCell>
              </StyledTableRow>
            )
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <>
      <Navbar />
      <div className="row">
        <Sidebar />
      </div>

      <main className="main-content position-relative h-100 border-radius-md contained ">
        <div className="container-fluid g-3 ">
          <div className="mx-2 mb-2">
            <Button
              variant={customerType === 'air' ? 'contained' : 'outlined'}
              onClick={() => changeCustomerType('air')}
              sx={{ mr: 1 }}
            >
              Air
            </Button>
            <Button
              variant={customerType === 'sewa' ? 'contained' : 'outlined'}
              onClick={() => changeCustomerType('sewa')}
            >
              Sewa
            </Button>
          </div>

          <h4 className="colorfix sizefix mx-2">Customers List all</h4>

          <div className="card px-2 pt-3 pb-3 mb-4 cardbg">
            <div className="container-fluid" style={{ backgroundColor: 'white' }}>
              <div className="container-fluid px-0 ">
                <div className="col-12">
                  <div className="container-fluid pb-4 pt-3 g-3">
                    <div className="row ">
                      <Box
                        className="row"
                        component="form"
                        sx={{ '& .MuiTextField-root': { m: 1, width: '40ch' } }}
                        noValidate
                        autoComplete="off"
                      >
                        <div className="col-5">
                          <TextField
                            required
                            id="customer-account-search"
                            label={`Search ${typeLabel} Customer by Account Number`}
                            value={accountSearch}
                            onChange={findCustomer}
                          />
                        </div>

                        <div className="col-5 mt-2">
                          <FormControl fullWidth>
                            <InputLabel id="customer-script-label">Choose Script</InputLabel>
                            <Select
                              labelId="customer-script-label"
                              id="customer-script"
                              label="Choose Script"
                              value={selectedScript}
                              onChange={findScript}
                            >
                              <MenuItem value="penjaja">Penjaja</MenuItem>
                              <MenuItem value="perumahan">Perumahan</MenuItem>
                            </Select>
                          </FormControl>
                        </div>

                        {selectedScript && (
                          <div className="col-8 mt-2 mx-2">
                            <FormControl fullWidth>
                              <InputLabel id="customer-location-label">
                                Choose Location ({selectedScript === 'perumahan' ? 'Perumahan' : 'Penjaja'})
                              </InputLabel>
                              <Select
                                labelId="customer-location-label"
                                id="customer-location"
                                label="Choose Location"
                                value={areaLocation}
                                onChange={findCustomerLocation}
                              >
                                {selectedAreas.map((kawasan) => (
                                  <MenuItem key={kawasan.kod_kawasan} value={kawasan.kod_kawasan}>
                                    {kawasan.nama_kawasan}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </div>
                        )}
                      </Box>

                      {accountSearch.trim() && searchPerformed && (
                        <div className="mt-3">
                          <legend><b>Customer Search Result</b></legend>
                          {renderCustomersTable(customer ? [customer] : [], true)}
                        </div>
                      )}

                      <div className="mt-4">
                        <legend><b>Total {typeLabel} Customer List by Area</b></legend>
                        <legend>
                          <Button
                            variant="contained"
                            size="large"
                            onClick={exportCustomersByArea}
                            disabled={!areaLocation}
                          >
                            Download Excel Report
                          </Button>
                        </legend>
                        {renderCustomersTable(area)}
                      </div>

                      <br className="pt-4" />
                      <hr />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default AllCustomers;
